import React, { useRef, useState } from "react";
import "./browser_packing_list_so.css";
import { SearchOutlined } from '@ant-design/icons';
import { useAuth } from "../../context/AuthContext";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table, Modal } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from "swr";

interface BrowserPackingListSoProps {
    onSelectSalesOrder: (salesOrderNo: string, deliverViaCode: string, deliverViaDescription: string, customerName: string, warehouseGroup: string, csCode: string) => void;
    onClose: () => void;
}

const BrowserPackingListSo: React.FC<BrowserPackingListSoProps> = ({ onSelectSalesOrder, onClose }) => {
    const { userData } = useAuth();
    const [isprogress, setIsprogress] = useState(false);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    interface DataType {
        key: string;
        sales_person: string;
        salesman_name: string;
        po_no: string
        so_no: string;
        date_picking: string;
        cs_code: string;
        deliver_via_code: string;
        deliver_via_description: string;
        warehouse_group: string;
        customer_name: string;
    }

    type DataIndex = keyof DataType;
    const fetcher = (url: string) => fetch(url).then(res => res.json());
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [tableFilters, setTableFilters] = useState<Record<string, string>>({});
    const [sorter, setSorter] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    const queryParams = new URLSearchParams({
        warehouseCode: userData?.warehouse_code || '',
        page: String(currentPage),
        limit: String(pageSize),
    });
    if (tableFilters.salesman_name) queryParams.append('salesman_name', tableFilters.salesman_name);
    if (tableFilters.po_no) queryParams.append('po_no', tableFilters.po_no);
    if (tableFilters.so_no) queryParams.append('so_no', tableFilters.so_no);
    if (tableFilters.date_picking) queryParams.append('date_picking', tableFilters.date_picking);
    if (tableFilters.cs_code) queryParams.append('cs_code', tableFilters.cs_code);
    if (sorter.field) {
        queryParams.append('sortField', sorter.field);
        if (sorter.order) queryParams.append('sortOrder', sorter.order);
    }

    const { data: apiResponse, isLoading } = useSWR<{ data: any[]; total: number }>(
        `${backendUrl}/api/picking-lists-for-packing?${queryParams.toString()}`,
        fetcher
    );
    const { data: customerData } = useSWR<any[]>(`${backendUrl}/api/customers`, fetcher);
    const customerMap = React.useMemo(() => {
        const map = new Map<string, string>();
        if (customerData) {
            customerData.forEach((c: any) => {
                if (c.cs_code && c.cs_name) {
                    map.set(c.cs_code, c.cs_name);
                }
            });
        }
        return map;
    }, [customerData]);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({ ...prev, [dataIndex]: selectedKeys[0] }));
        setCurrentPage(1);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setTableFilters(prev => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        setCurrentPage(1);
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex).replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Salesman',
            dataIndex: 'salesman_name',
            key: 'salesman_name',
            ...getColumnSearchProps('salesman_name'),
            sorter: true,
            render: (text, record) => {
                const displaySalesman = record.salesman_name || record.sales_person || '-';
                return searchedColumn === 'salesman_name' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={displaySalesman ? displaySalesman.toString() : ''}
                    />
                ) : (
                    displaySalesman
                );
            },
        },
        {
            title: 'PO No',
            dataIndex: 'po_no',
            key: 'po_no',
            ...getColumnSearchProps('po_no'),
            sorter: true,
        },
        {
            title: 'Sales Order No',
            dataIndex: 'so_no',
            key: 'so_no',
            ...getColumnSearchProps('so_no'),
            sorter: true,
        },
        {
            title: 'Date',
            dataIndex: 'date_picking',
            key: 'date_picking',
            ...getColumnSearchProps('date_picking'),
            sorter: true,
        },
        {
            title: 'Customer',
            dataIndex: 'cs_code',
            key: 'cs_code',
            ...getColumnSearchProps('cs_code'),
            sorter: true,
            render: (text, record) => {
                const csName = record.customer_name || text;
                return searchedColumn === 'cs_code' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={csName ? csName.toString() : ''}
                    />
                ) : (
                    csName
                );
            },
        },
    ];

    const formattedData: DataType[] = apiResponse?.data ? apiResponse.data.map((item: any) => ({
        key: item.id.toString(),
        sales_person: item.sales_person, 
        salesman_name: item.salesman_name || item.sales_person, 
        po_no: item.purchase_order_no,
        so_no: item.so_no,
        date_picking: item.date_picking,
        cs_code: item.cs_code,
        deliver_via_code: item.deliver_via_code,
        deliver_via_description: item.deliver_via_description,
        warehouse_group: item.warehouse_group,
        customer_name: item.customer_name,

    })) : [];

    return (

        <div id="iqfi" style={transformStyle}>
            <div className="gjs-grid-row" id="ia3732s">
                <div className="gjs-grid-column" id="ibfdfl">
                    <Table<DataType>
                        scroll={{ x: 'max-content' }}
                        style={{ whiteSpace: 'nowrap' }}
                        columns={columns}
                        dataSource={formattedData}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: apiResponse?.total || 0,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20'],
                        }}
                        loading={isLoading || isprogress}
                        onChange={(paginationInfo, filtersInfo, sorterInfo: any) => {
                            setCurrentPage(paginationInfo.current || 1);
                            setPageSize(paginationInfo.pageSize || 5);
                            if (sorterInfo && sorterInfo.field) {
                                setSorter({
                                    field: sorterInfo.field,
                                    order: sorterInfo.order,
                                });
                            } else {
                                setSorter({});
                            }
                        }}
                        onRow={(record) => {
                            return {
                                onClick: async () => {
                                    if (isprogress) return;
                                    setIsprogress(true);
                                    try {
                                        const response = await fetch(`${backendUrl}/api/packing-list/check-draft?soNo=${encodeURIComponent(record.so_no)}`);
                                        const result = await response.json();
                                        if (result.exists) {
                                            Modal.warning({
                                                title: 'Peringatan',
                                                content: `Nomor Sales Order ${record.so_no} sudah di packing dalam kondisi draft dengan nomor packing ${result.packing_list_no}, selesaikan terlebih dahulu !`,
                                                okText: 'OK'
                                            });
                                        } else {
                                            onSelectSalesOrder(record.so_no, record.deliver_via_code, record.deliver_via_description, record.customer_name, record.warehouse_group, record.cs_code);
                                            onClose();
                                        }
                                    } catch (error) {
                                        console.error("Gagal memeriksa draft packing list:", error);
                                        onSelectSalesOrder(record.so_no, record.deliver_via_code, record.deliver_via_description, record.customer_name, record.warehouse_group, record.cs_code);
                                        onClose();
                                    } finally {
                                        setIsprogress(false);
                                    }
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserPackingListSo;